// src/composables/useDropWizard.js
import { computed, getCurrentScope, onScopeDispose, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import {
  allStepsValid,
  defaultWizardData,
  newMethodDraft,
  normalizeForRhythm,
  reminderBody,
  stepForKey,
  stepValid,
  visibleSteps
} from '@/config/dropWizardSteps'
import { buildPreviewPayload, buildSetupPayload } from '@/helpers/dropWizardPayload'
import { clearDraft, mergeDraft, readDraft, writeDraft } from '@/helpers/wizardDraft'
import api from '@/store/api'

const GENERIC_ERROR = 'Something went wrong creating this drop. Nothing was created.'
const PREVIEW_ERROR = 'The dates could not be worked out. Check the answers so far.'
const FIRST_STEP = 'what'
const PREVIEW_DELAY_MS = 400

const hydrateDraft = (key, saved) => (key === 'newMethod' ? mergeDraft(newMethodDraft(), saved) : undefined)

const serverErrors = (error, fallback) => {
  const messages = error?.response?.data?.errors
  return Array.isArray(messages) && messages.length ? messages : [fallback]
}

export function useDropWizard() {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const draftKey = `dropWizard:${store.getters.loggedInUserId}`

  const draft = readDraft(draftKey)
  const data = reactive(mergeDraft(defaultWizardData(), draft?.data, { hydrate: hydrateDraft }))

  const steps = computed(() => visibleSteps(data))
  const questionCount = computed(() => steps.value.filter(s => s.key !== 'review').length)
  const canReview = computed(() => allStepsValid(data))

  const firstUnfinishedStep = () => steps.value.find(s => s.key !== 'review' && !stepValid(s.key, data))?.key
  const openingStep = requested => (requested === 'review' ? firstUnfinishedStep() ?? 'review' : requested)

  const requested = stepForKey(route.params.step)?.key ?? stepForKey(draft?.stepKey)?.key ?? FIRST_STEP
  const stepKey = ref(openingStep(requested))
  const step = computed(() => steps.value.find(s => s.key === stepKey.value) ?? steps.value[0])
  const valid = computed(() => stepValid(stepKey.value, data))
  const saving = ref(false)
  const errors = ref([])

  // Reminder ids are minted fresh for every default draft, so they are left out of the comparison.
  const withoutReminderIds = value => ({
    ...value,
    reminders: value.reminders.map(reminder => ({ ...reminder, id: null }))
  })
  const pristine = () =>
    stepKey.value === FIRST_STEP &&
    JSON.stringify(withoutReminderIds(data)) === JSON.stringify(withoutReminderIds(defaultWizardData()))

  const persist = () => {
    if (pristine()) clearDraft(draftKey)
    else writeDraft(draftKey, { data: JSON.parse(JSON.stringify(data)), stepKey: stepKey.value })
  }
  watch(data, persist, { deep: true })
  watch(
    () => data.rhythm,
    () => normalizeForRhythm(data)
  )

  const showStep = key => {
    stepKey.value = key
    errors.value = []
    router.replace({ params: { step: key }, query: route.query })
    persist()
  }

  const go = key => {
    if (!stepForKey(key) || key === stepKey.value) return
    if (key === 'review' && !canReview.value) return
    showStep(key)
  }

  const next = () => {
    if (!valid.value) return
    const following = steps.value[step.value.index + 1]
    if (following) go(openingStep(following.key))
  }

  const back = () => {
    const previous = steps.value[step.value.index - 1]
    if (previous) go(previous.key)
  }

  const reset = () => {
    Object.assign(data, defaultWizardData())
    stepKey.value = FIRST_STEP
    errors.value = []
    clearPreview()
    clearDraft(draftKey)
    router.replace({ params: { step: FIRST_STEP }, query: route.query })
  }

  // preview

  const preview = ref(null)
  const previewReminderIds = ref([])
  const previewOk = ref(false)
  const previewLoading = ref(false)
  const previewError = ref('')
  const fallbackMethodId = ref(null)
  let previewTimer = null
  let previewSerial = 0

  const setFallbackMethodId = id => {
    fallbackMethodId.value = id ? String(id) : null
  }

  // Bumping the serial makes any request still in flight land nowhere.
  const clearPreview = () => {
    previewSerial += 1
    clearTimeout(previewTimer)
    preview.value = null
    previewReminderIds.value = []
    previewOk.value = false
    previewLoading.value = false
    previewError.value = ''
  }

  // The preview payload leaves out reminders without text, so the server numbers
  // the rest in order. These ids say which draft reminder each index means.
  const sentReminderIds = () =>
    data.reminders.filter(reminder => reminderBody(reminder, data).trim().length > 0).map(reminder => reminder.id)

  const runPreview = async () => {
    const payload = buildPreviewPayload(data, { fallbackMethodId: fallbackMethodId.value })
    if (!payload) {
      clearPreview()
      return
    }
    const reminderIds = sentReminderIds()
    const serial = (previewSerial += 1)
    previewOk.value = false
    previewLoading.value = true
    try {
      const response = await api.call(
        'POST',
        'bottle_cycles/setup/preview',
        { bottle_cycle_setup: payload },
        { save: false, receive: false }
      )
      if (serial !== previewSerial) return
      preview.value = response.data.data
      previewReminderIds.value = reminderIds
      previewOk.value = true
      previewError.value = ''
    } catch (error) {
      if (serial !== previewSerial) return
      previewError.value = serverErrors(error, PREVIEW_ERROR)[0]
    } finally {
      if (serial === previewSerial) previewLoading.value = false
    }
  }

  const schedulePreview = () => {
    clearTimeout(previewTimer)
    previewTimer = setTimeout(runPreview, PREVIEW_DELAY_MS)
  }
  watch(data, schedulePreview, { deep: true })
  watch(fallbackMethodId, schedulePreview)
  if (getCurrentScope()) onScopeDispose(() => clearTimeout(previewTimer))

  // submit

  const submit = async () => {
    saving.value = true
    errors.value = []
    try {
      const response = await api.call('POST', 'bottle_cycles/setup', { bottle_cycle_setup: buildSetupPayload(data) })
      clearDraft(draftKey)
      return response.data.data.id
    } catch (error) {
      errors.value = serverErrors(error, GENERIC_ERROR)
      return null
    } finally {
      saving.value = false
    }
  }

  return {
    data,
    steps,
    questionCount,
    step,
    stepKey,
    valid,
    canReview,
    next,
    back,
    go,
    reset,
    submit,
    saving,
    errors,
    preview,
    previewReminderIds,
    previewOk,
    previewLoading,
    previewError,
    setFallbackMethodId
  }
}
